import { HttpClient } from '@angular/common/http';
import { EventEmitter, inject, Injectable, LOCALE_ID, OnDestroy } from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { SHARED_ENVIRONMENT } from '@k12/environment';
import { DatePipe } from '@angular/common';
import { SignalRService } from '@k12/signalr';
import { MessageCenterCategory, SendAccountMessagePayload, TopicMessage } from '@k12/models';
import { CreateTopicPayload, Message, Thread } from './message-center/message-center.model';

interface ArchiveAccountMessageTopicsPayload {
  accountMessageTopicId: string;
}

interface AccountMessageTopicReadPayload {
  accountMessageTopicId: string;
  isRead: boolean;
}

interface CreateAdminAccountMessageTopicPayload {
  assignedToId?: string | null;
  childCategoryId?: string | null;
  childCategoryName?: string;
  description?: string;
  Description?: string;
  parentCategoryId?: string;
  parentCategoryName?: string;
  participantId: string;
  programPartnerId?: string;
  programPartnerTypeId?: number | null;
}

interface PartnerSendAccountMessagePayload {
  accountMessageTopicId: string;
  message: string;
  recipientId: string | null;
  senderId: string | null;
}

interface NewMessageSignalREvent {
  msg: unknown;
  user: unknown;
}

interface MessageReadSignalREvent {
  body: Partial<TopicMessage>;
  recipient?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MessageCenterService implements OnDestroy {
  private readonly sharedEnvironment = inject(SHARED_ENVIRONMENT);
  private readonly apiUrl = this.sharedEnvironment.adminApiUrl;
  private readonly locale: string = inject<string>(LOCALE_ID);
  private readonly signalRService: SignalRService = inject(SignalRService);
  private readonly ngDestroy: Subject<void> = new Subject<void>();
  private readonly http: HttpClient = inject(HttpClient);

  // signalR Events
  public unReadUpdate = new EventEmitter<string>();
  public unclassified = new EventEmitter<string>();
  public newUnassignedTopic = new EventEmitter<string>();
  public topicAssigned = new EventEmitter<string>();
  public messageReply = new EventEmitter<string>();
  public topicArchived = new EventEmitter<string>();
  public newMessage = new EventEmitter<NewMessageSignalREvent>();
  public messageRead = new EventEmitter<MessageReadSignalREvent>();

  constructor() {
    setTimeout(() => {
      this.registerSignalREvents();
    }, 100);
  }

  ngOnDestroy(): void {
    this.ngDestroy.next();
    this.ngDestroy.complete();
  }

  public ArchiveAccountMessageTopics(payload: ArchiveAccountMessageTopicsPayload): Observable<unknown> {
    return this.http.post<unknown>(`${this.apiUrl}/comms/accountMessageTopicArchive`, payload);
  }

  public CreateAccountMessageTopic(payload: CreateTopicPayload): Observable<Thread> {
    return this.http.post<Thread>(`${this.apiUrl}/comms/accountMessageTopic`, payload);
  }

  // Admin quick-chat (user record / work queue) must use admcomms so the topic is
  // associated with the program partner instead of the signed-in admin account.
  public CreateAdminAccountMessageTopic(payload: CreateAdminAccountMessageTopicPayload): Observable<unknown> {
    return this.http.post<unknown>(`${this.apiUrl}/admcomms/accountMessageTopic`, payload);
  }

  public GetAllAccountMessagesAndTopics(): Observable<Thread[]> {
    return this.http.get<Thread[]>(`${this.apiUrl}/comms/allAccountMessagesAndTopics`);
  }

  public GetAllTopics(programPartnerTypeId: number | null): Observable<MessageCenterCategory[]> {
    return this.GetMessageTopicsForProgramPartnerType(programPartnerTypeId);
  }

  public GetMessageTopicsForProgramPartnerType(
    programPartnerTypeId: number | null
  ): Observable<MessageCenterCategory[]> {
    return this.http.get<MessageCenterCategory[]>(`${this.apiUrl}/comms/messagingTopics/${programPartnerTypeId}`);
  }

  public SendAccountMessage(payload: PartnerSendAccountMessagePayload): Observable<Message> {
    return this.http.post<Message>(`${this.apiUrl}/comms/accountMessage`, payload);
  }

  public SendAdminAccountMessage(payload: SendAccountMessagePayload): Observable<unknown> {
    return this.http.post<unknown>(`${this.apiUrl}/admcomms/accountMessage`, payload);
  }

  public ToggleAccountMessageTopicAsRead(payload: AccountMessageTopicReadPayload): Observable<unknown> {
    return this.http.post<unknown>(`${this.apiUrl}/comms/accountMessageTopicRead`, payload);
  }

  convertUtcToLocal(utcDateTimeString: string | Date): Date {
    const utcDate = new Date(utcDateTimeString);
    return new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60 * 1000);
  }

  public getFormattedTime(date: Date | string | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const pipe = new DatePipe(this.locale);
    return d < twentyFourHoursAgo || d.getDay() !== now.getDay() ? (pipe.transform(d, 'MM/dd/yyyy') ?? '') : (pipe.transform(d, 'h:mm a') ?? '');
  }

  private registerSignalREvents(): void {
    this.signalRService.signalRConnectionStatus
      .pipe(takeUntil(this.ngDestroy))
      .subscribe((res) => {
        if (res === 'open') {
          this.attachSignalREvents();
        } else {
          this.detachSignalREvents();
        }
      });
  }

  private attachSignalREvents(): void {
    this.signalRService.hubConnection?.on('UnReadUpdate', (msg: string) => {
      this.unReadUpdate.emit(msg);
    });

    this.signalRService.hubConnection?.on('Unclassified', (msg: string) => {
      this.unclassified.emit(msg);
    });

    this.signalRService.hubConnection?.on('NewUnassignedTopic', (msg: string) => {
      this.newUnassignedTopic.emit(msg);
    });

    this.signalRService.hubConnection?.on('TopicAssigned', (msg: string) => {
      this.topicAssigned.emit(msg);
    });

    this.signalRService.hubConnection?.on('MessageReply', (msg: string) => {
      this.messageReply.emit(msg);
    });

    this.signalRService.hubConnection?.on('TopicArchived', (msg: string) => {
      this.topicArchived.emit(msg);
    });

    this.signalRService.hubConnection?.on('MessageRead', (msg: string) => {
      this.messageRead.emit(JSON.parse(msg) as MessageReadSignalREvent);
    });

    this.signalRService.hubConnection?.on('newMessage', (user: unknown, msg: unknown) => {
      this.newMessage.emit({ user, msg });
    });
  }

  private detachSignalREvents(): void {
    this.signalRService.hubConnection?.off('UnReadUpdate');
    this.signalRService.hubConnection?.off('Unclassified');
    this.signalRService.hubConnection?.off('NewUnassignedTopic');
    this.signalRService.hubConnection?.off('TopicAssigned');
    this.signalRService.hubConnection?.off('MessageReply');
    this.signalRService.hubConnection?.off('TopicArchived');
    this.signalRService.hubConnection?.off('MessageRead');
    this.signalRService.hubConnection?.off('newMessage');
  }
}
